import React, { useState, useEffect } from 'react';
import { ROLES, canManageUser } from '../context/UsersContext';

const ModalWrapper = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const UserFormModal = ({ isOpen, user, currentUser, onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', email: '', role: 'sales', password: '' });

  useEffect(() => {
    if (user) {
      setForm({ name: user.name, email: user.email, role: user.role, password: '' });
    } else {
      setForm({ name: '', email: '', role: 'sales', password: '' });
    }
  }, [user, isOpen]);

  const availableRoles = Object.entries(ROLES)
    .filter(([roleKey]) => canManageUser(currentUser.role, roleKey))
    .map(([key, val]) => ({ key, label: val.label }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { name: form.name, email: form.email, role: form.role };
    if (!user && form.password) data.password = form.password;
    onSubmit(data);
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title={user ? 'Edit User' : 'Add New User'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Full Name *</label>
          <input
            required
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email *</label>
          <input
            required
            type="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Role *</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-sm"
            value={form.role}
            onChange={e => setForm({ ...form, role: e.target.value })}
          >
            {availableRoles.map(r => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
        </div>
        {!user && (
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Password *</label>
            <input
              required
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="Set a password"
            />
          </div>
        )}
        <div className="pt-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
            Cancel
          </button>
          <button type="submit" className="px-5 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium text-sm">
            {user ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default UserFormModal;